import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Calendar, 
  Clock, 
  Users, 
  DollarSign, 
  Car, 
  MessageSquare,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../services/fetchWithAuth';
import LocationSearch from '../components/LocationSearch';
import SimpleMap from '../components/SimpleMap';
import { FaMapMarkerAlt } from 'react-icons/fa';

const CreateAnnouncement = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    from: '',
    to: '',
    date: '',
    time: '',
    price: '',
    seats: 1,
    vehicle: '',
    description: '',
    preferences: []
  });
  const [errors, setErrors] = useState({});
  const [fromLocation, setFromLocation] = useState(null);
  const [toLocation, setToLocation] = useState(null);
  const [manualFrom, setManualFrom] = useState('');
  const [manualTo, setManualTo] = useState('');
  const [useManualFrom, setUseManualFrom] = useState(false);
  const [useManualTo, setUseManualTo] = useState(false);
  const [pickFromOnMap, setPickFromOnMap] = useState(false);
  const [pickToOnMap, setPickToOnMap] = useState(false);
  const [fromAddress, setFromAddress] = useState("");
  const [toAddress, setToAddress] = useState("");

  const vehicleOptions = [
    'Honda City', 'Maruti Swift', 'Hyundai i20', 'Tata Nexon', 'Maruti Alto',
    'Toyota Innova', 'Mahindra XUV500', 'Honda Amaze', 'Maruti Dzire', 'Other'
  ];

  const preferenceOptions = [
    'Non-smokers only', 'Music lovers welcome', 'Professional environment',
    'Student-friendly', 'Flexible timing', 'Strict timing', 'Luggage space available'
  ];

  useEffect(() => {
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setFormData(prev => ({
      ...prev,
      date: tomorrow.toISOString().split('T')[0]
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePreferenceToggle = (preference) => {
    setFormData(prev => ({
      ...prev,
      preferences: prev.preferences.includes(preference)
        ? prev.preferences.filter(p => p !== preference)
        : [...prev.preferences, preference]
    }));
  };

  const handleFromLocationSelect = (place) => {
    setFormData(prev => ({
      ...prev,
      from: place.name
    }));
    setFromLocation(place);
    
    // Clear error
    if (errors.from) {
      setErrors(prev => ({
        ...prev,
        from: ''
      }));
    }
  };

  const handleToLocationSelect = (place) => {
    setFormData(prev => ({
      ...prev,
      to: place.name
    }));
    setToLocation(place);
    
    // Clear error
    if (errors.to) {
      setErrors(prev => ({
        ...prev,
        to: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    // Use the correct values depending on manual toggle
    const fromValue = useManualFrom ? manualFrom.trim() : formData.from.trim();
    const toValue = useManualTo ? manualTo.trim() : formData.to.trim();
    if (!fromValue) newErrors.from = 'From location is required';
    if (!toValue) newErrors.to = 'To location is required';
    if (fromValue && toValue && fromValue === toValue) newErrors.to = 'From and To locations cannot be the same';
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    if (!formData.price || formData.price <= 0) newErrors.price = 'Valid price is required';
    if (!formData.vehicle.trim()) newErrors.vehicle = 'Vehicle is required';
    if (formData.seats < 1 || formData.seats > 6) newErrors.seats = 'Seats must be between 1 and 6';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      // Combine date and time into ISO8601 rideTime
      const rideTime = new Date(`${formData.date}T${formData.time}`);
      // Build timeWindow: 30 min before and after rideTime
      const startWindow = new Date(rideTime.getTime() - 30 * 60000);
      const endWindow = new Date(rideTime.getTime() + 30 * 60000);
      // Build sourceLocation object
      let sourceLocation = null;
      if (useManualFrom && manualFrom.trim()) {
        sourceLocation = {
          address: manualFrom.trim(),
          coordinates: [0, 0] // Placeholder if no real coordinates
        };
      } else if (fromLocation && fromLocation.geometry && fromLocation.formatted_address) {
        sourceLocation = {
          coordinates: [
            fromLocation.geometry.location.lng,
            fromLocation.geometry.location.lat
          ],
          address: fromLocation.formatted_address
        };
      }
      // Build vehicleInfo object
      const vehicleInfo = formData.vehicle ? { model: formData.vehicle } : undefined;
      // Prepare announcement data for backend
      const announcementData = {
        sourceLocation,
        destination: useManualTo && manualTo.trim() ? manualTo.trim() : formData.to,
        rideTime: rideTime.toISOString(),
        timeWindow: {
          start: startWindow.toISOString(),
          end: endWindow.toISOString()
        },
        connectCost: Number(formData.price),
        maxPassengers: Number(formData.seats),
        description: formData.description,
        tags: formData.preferences,
        vehicleInfo
      };
      const response = await fetchWithAuth('/api/announcements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(announcementData)
      }, logout);
      if (response.ok) {
        setSuccess(true);
        localStorage.setItem('refreshAnnouncements', 'true');
        setTimeout(() => {
          navigate('/announcements');
        }, 2000);
      } else {
        const errorData = await response.json();
        if (errorData.errors) {
          setErrors({ submit: errorData.errors.map(e => e.msg).join(', ') });
          console.error('Create announcement error:', errorData.errors);
        } else {
          setErrors({ submit: errorData.error || 'Failed to create announcement' });
          console.error('Create announcement error:', errorData);
        }
      }
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to create announcement' });
      console.error('Create announcement error:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateEstimatedPrice = () => {
    // Simple price calculation based on distance
    if (!fromLocation || !toLocation) return '150-300';
    
    // This would be replaced with actual distance calculation
    return '150-300';
  };

  if (success) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Announcement Created!</h2>
          <p className="text-gray-600">Your ride announcement has been successfully created and is now visible to other users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Share a Ride</h1>
        <p className="text-gray-600">Create a ride announcement and find passengers to split costs</p>
      </div>

      <div className="bg-white rounded-lg shadow">
        {/* Show submit error if present */}
        {errors.submit && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {errors.submit}
          </div>
        )}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Ride Details</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Route Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* From Location */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">From</label>
              <div className="flex items-center space-x-2">
                <button type="button" onClick={() => setUseManualFrom(f => !f)} className="text-blue-600 underline text-xs">
                  {useManualFrom ? 'Use Location Picker' : 'Type Manually'}
                </button>
              </div>
              {useManualFrom ? (
                <div className="relative">
                  <input
                    type="text"
                    name="manualFrom"
                    value={manualFrom}
                    onChange={e => setManualFrom(e.target.value)}
                    className="w-full border rounded px-3 py-2 mt-1 pr-10"
                    placeholder="Enter starting location"
                  />
                  <FaMapMarkerAlt
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 cursor-pointer"
                    size={18}
                    onClick={() => setPickFromOnMap(true)}
                    title="Pick on Map"
                  />
                </div>
              ) : pickFromOnMap ? (
                <SimpleMap
                  userLocation={fromLocation}
                  onSelectLocation={({ lat, lng, address }) => {
                    setFromLocation({ lat, lng });
                    setFromAddress(address);
                    setPickFromOnMap(false);
                  }}
                  className="mt-2"
                />
              ) : (
                <div className="relative">
                  <LocationSearch
                    value={fromAddress}
                    onSelect={place => {
                      setFromLocation(place.location);
                      setFromAddress(place.address || place.label || "");
                    }}
                    placeholder="Search starting location"
                  />
                  <FaMapMarkerAlt
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 cursor-pointer"
                    size={18}
                    onClick={() => setPickFromOnMap(true)}
                    title="Pick on Map"
                  />
                  {fromLocation && (
                    <SimpleMap userLocation={fromLocation} className="mt-2" />
                  )}
                </div>
              )}
              {errors.from && <div className="text-red-500 text-xs mt-1">{errors.from}</div>}
            </div>

            {/* To Location */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">To</label>
              <div className="flex items-center space-x-2">
                <button type="button" onClick={() => setUseManualTo(f => !f)} className="text-blue-600 underline text-xs">
                  {useManualTo ? 'Use Location Picker' : 'Type Manually'}
                </button>
              </div>
              {useManualTo ? (
                <div className="relative">
                  <input
                    type="text"
                    name="manualTo"
                    value={manualTo}
                    onChange={e => setManualTo(e.target.value)}
                    className="w-full border rounded px-3 py-2 mt-1 pr-10"
                    placeholder="Enter destination"
                  />
                  <FaMapMarkerAlt
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 cursor-pointer"
                    size={18}
                    onClick={() => setPickToOnMap(true)}
                    title="Pick on Map"
                  />
                </div>
              ) : pickToOnMap ? (
                <SimpleMap
                  userLocation={toLocation}
                  onSelectLocation={({ lat, lng, address }) => {
                    setToLocation({ lat, lng });
                    setToAddress(address);
                    setPickToOnMap(false);
                  }}
                  className="mt-2"
                />
              ) : (
                <div className="relative">
                  <LocationSearch
                    value={toAddress}
                    onSelect={place => {
                      setToLocation(place.location);
                      setToAddress(place.address || place.label || "");
                    }}
                    placeholder="Search destination"
                  />
                  <FaMapMarkerAlt
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 cursor-pointer"
                    size={18}
                    onClick={() => setPickToOnMap(true)}
                    title="Pick on Map"
                  />
                  {toLocation && (
                    <SimpleMap userLocation={toLocation} className="mt-2" />
                  )}
                </div>
              )}
              {errors.to && <div className="text-red-500 text-xs mt-1">{errors.to}</div>}
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.date ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.date && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.date}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.time ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.time && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.time}
                </p>
              )}
            </div>
          </div>

          {/* Price and Seats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price per seat (₹) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="Enter price"
                  min="1"
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.price ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.price && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.price}
                </p>
              )}
              {formData.from && formData.to && (
                <p className="mt-1 text-sm text-gray-500">
                  Estimated price: ₹{calculateEstimatedPrice()}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Seats <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  name="seats"
                  value={formData.seats}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.seats ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <option key={num} value={num}>{num} seat{num !== 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
              {errors.seats && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.seats}
                </p>
              )}
            </div>
          </div>

          {/* Vehicle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vehicle <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Car className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                name="vehicle"
                value={formData.vehicle}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.vehicle ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select your vehicle</option>
                {vehicleOptions.map(vehicle => (
                  <option key={vehicle} value={vehicle}>{vehicle}</option>
                ))}
              </select>
            </div>
            {errors.vehicle && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.vehicle}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Add any additional details about your ride..."
                rows={4}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Preferences */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Preferences (Optional)
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {preferenceOptions.map(preference => (
                <label key={preference} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.preferences.includes(preference)}
                    onChange={() => handlePreferenceToggle(preference)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{preference}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/announcements')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Create Announcement</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAnnouncement; 